package Network

import DataBase.DBQuery
import DataBase.UINKDBInterface
import Structs.ClientTaxNote
import Structs.InformationBase
import Structs.Respone
import Utils.DocType

/**
 * Before sharing / PDF / print, retry חשבונית ישראל signing when [User.hesh_israel] is enabled
 * and the invoice is missing [ClientTaxNote.taxConfirmation] (same threshold as [DataBase.NKDBInterface.hashIsraelImplyMulti]).
 */
object HashIsraelTaxConfirmationHelper {

    private val OUTPUT_TASKS = setOf(
        Tasks.GET_CLIENT_TAX_PDF,
        Tasks.SHARE_WHATSAPP_CLIENT_TAX_PDF,
        Tasks.SHARE_EMAIL_CLIENT_TAX_PDF,
        Tasks.CALCULATE_CLIENT_MASS_DOC_PDF,
        Tasks.GET_DOCUMENT_PDF,
        Tasks.SEND_MSG_ALL,
    )

    private val clientHashIsraelTypes = setOf(
        DocType.TAX_NOTE.state,
        DocType.TAX_PAY_NOTE.state,
        DocType.TAX_NOTE_CANCEL.state,
    )

    private fun isHeshIsraelEnabled(): Boolean =
        UINKDBInterface.activeDB.getUser().first?.hesh_israel == 1

    fun shouldEnsureBeforeTask(taskId: Tasks?): Boolean =
        taskId != null && taskId in OUTPUT_TASKS

    suspend fun ensureBeforeTask(taskId: Tasks?, params: HashMap<String, Any>) {
        if (!shouldEnsureBeforeTask(taskId)) return
        val refs = extractTaxNoteRefs(taskId!!, params) ?: return
        ensureForRefs(refs)
    }

    suspend fun ensureForTaxNotes(notes: List<ClientTaxNote>, sup: Boolean = false) {
        if (!isHeshIsraelEnabled()) return
        ensureForRefs(notes.map { TaxNoteRef(it.id, it.type, sup) })
    }

    private data class TaxNoteRef(val id: Int, val type: Int, val sup: Boolean)

    private suspend fun ensureForRefs(refs: List<TaxNoteRef>) {
        if (!isHeshIsraelEnabled()) return
        refs.distinctBy { "${it.id}_${it.type}_${it.sup}" }.forEach { ensureSingle(it) }
    }

    private suspend fun ensureSingle(ref: TaxNoteRef) {
        val limit = UINKDBInterface.activeDB.TAX_ISRAEL_LIMIT
        val note = UINKDBInterface.activeDB.getClientTaxNote(
            id = ref.id,
            type = ref.type,
            sup = ref.sup,
        ).first.firstOrNull() ?: return

        if (note.taxConfirmation.isNotBlank()) return
        if (note.total_value < limit) return

        val signParams = hashMapOf<String, Any>(
            "id" to ref.id,
            "type" to ref.type,
        )
        if (ref.sup) signParams["sup"] = true

        @Suppress("UNCHECKED_CAST")
        val signResult = Tasks.HASH_ISRAEL_SIGN.applyTask(signParams)
            as? Respone<Pair<ClientTaxNote?, String>, DBQuery>

        val signed = signResult?.first?.first
        if (signResult?.second == DBQuery.SUCCESS && !signed?.taxConfirmation.isNullOrBlank()) {
            refreshTaxNote(ref)
        }
    }

    private suspend fun refreshTaxNote(ref: TaxNoteRef) {
        val refreshParams = hashMapOf<String, Any>(
            "ids" to listOf(ref.id),
            "type" to ref.type,
            "full" to true,
        )
        if (ref.sup) refreshParams["sup"] = true
        Tasks.GET_CLIENT_TAX_NOTE.applyTask(refreshParams)
    }

    private fun extractTaxNoteRefs(taskId: Tasks, params: HashMap<String, Any>): List<TaxNoteRef>? {
        val requestedSup = when (val v = params["sup"]) {
            is Boolean -> v
            is Number -> v.toInt() != 0
            is String -> v.equals("true", ignoreCase = true)
            else -> false
        }
        return when (taskId) {
            Tasks.GET_CLIENT_TAX_PDF,
            Tasks.SHARE_WHATSAPP_CLIENT_TAX_PDF,
            Tasks.SHARE_EMAIL_CLIENT_TAX_PDF -> {
                if (requestedSup) return null
                val id = params["id"] as? Int ?: return null
                val type = params["type"] as? Int ?: DocType.TAX_NOTE.state
                if (type !in clientHashIsraelTypes) return null
                listOf(TaxNoteRef(id, type, false))
            }

            Tasks.CALCULATE_CLIENT_MASS_DOC_PDF -> {
                val ids = params["ids"] as? List<*> ?: return null
                val docs = params["docs"] as? List<*> ?: return null
                if (ids.size != docs.size) return null
                ids.zip(docs).mapNotNull { (rawId, rawDoc) ->
                    val id = rawId as? Int ?: return@mapNotNull null
                    val docType = rawDoc as? Int ?: return@mapNotNull null
                    if (docType in clientHashIsraelTypes) TaxNoteRef(id, docType, false) else null
                }.takeIf { it.isNotEmpty() }
            }

            Tasks.GET_DOCUMENT_PDF -> {
                val doc = params["doc"] as? InformationBase ?: return null
                if (doc !is ClientTaxNote) return null
                if (!doc.isClient) return null
                val docType = doc.getConnectedDocType()
                if (docType !in clientHashIsraelTypes) return null
                listOf(TaxNoteRef(doc.id, doc.type, false))
            }

            Tasks.SEND_MSG_ALL -> {
                val ids = params["ids"] as? List<*> ?: return null
                val docTypes = params["doc_type"] as? List<*> ?: return null
                if (ids.isEmpty() || docTypes.isEmpty()) return null
                if (ids.size == docTypes.size) {
                    return ids.zip(docTypes).mapNotNull { (rawId, rawType) ->
                        val id = rawId as? Int ?: return@mapNotNull null
                        val docType = rawType as? Int ?: return@mapNotNull null
                        if (docType in clientHashIsraelTypes) TaxNoteRef(id, docType, false) else null
                    }.takeIf { it.isNotEmpty() }
                }
                if (docTypes.size == 1) {
                    val docType = docTypes.first() as? Int ?: return null
                    if (docType !in clientHashIsraelTypes) return null
                    return ids.mapNotNull { rawId ->
                        val id = rawId as? Int ?: return@mapNotNull null
                        TaxNoteRef(id, docType, false)
                    }.takeIf { it.isNotEmpty() }
                }
                null
            }

            else -> null
        }
    }
}
